import { useMemo, useState } from 'react';

import type { ArInvoiceListRow, CreateArPdcInput } from '@dcc-erp/shared';
import { CreateArPdcSchema, dateToInputValue, fmt, toNumber } from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import { useArInvoices, useCreateArPdc, useCustomers } from '../../lib/queries';
import { SearchSelect } from '../SearchSelect';
import { modalBody, modalError, modalFooter, modalLabel } from './modal-style';
import { allocationError } from './receipt-allocation';
import { AppModal } from '../AppModal';

// =============================================================================
// Register a post-dated cheque (เช็คลงวันที่ล่วงหน้า / PDC) — customer-first.
//
// CreatePdcModal (container) owns customer selection + data fetching; the inner
// CreatePdcForm (presentational, router/data-free) owns the cheque fields + the
// tickable open-invoice allocation grid + the running allocated total so the
// submit payload is unit-testable without a query client — exactly the split used
// by RecordReceiptModal / CreateBillingNoteModal. A PDC posts NO journal entry;
// it just registers the cheque + the invoices it is intended to settle when it
// clears. Σ allocations ≤ the cheque amount (the form blocks an over-allocation).
// =============================================================================

// Minimal shape the form reads — accept full list rows but only depend on these
// so callers (and tests) can pass lean fixtures.
type OpenInvoice = Pick<
  ArInvoiceListRow,
  'id' | 'voucherNo' | 'invoiceDate' | 'totalAmount' | 'paidAmount' | 'creditedAmount' | 'dueDate'
>;

export interface CreatePdcFormProps {
  openInvoices: OpenInvoice[];
  onSubmit: (payload: CreateArPdcInput) => void;
  submitting: boolean;
  // Container-supplied; the customer is already chosen upstream. Defaults to ''
  // so the presentational form can be rendered in isolation (tests assert only
  // the submitted payload shape).
  customerId?: string;
}

export function CreatePdcForm({
  openInvoices,
  onSubmit,
  submitting,
  customerId = '',
}: CreatePdcFormProps) {
  const today = new Date();
  const [chequeNo, setChequeNo] = useState('');
  const [bankName, setBankName] = useState('');
  const [bankBranch, setBankBranch] = useState('');
  const [chequeDate, setChequeDate] = useState('');
  const [amount, setAmount] = useState<number>(0);
  const [receivedDate, setReceivedDate] = useState(dateToInputValue(today));
  const [note, setNote] = useState('');
  // Per-invoice allocation: a ticked row defaults its amount to that invoice's
  // outstanding; unticking drops it from the submitted array.
  const [ticked, setTicked] = useState<Record<string, boolean>>({});
  const [amounts, setAmounts] = useState<Record<string, number>>({});

  const outstandingById = useMemo(() => {
    const m: Record<string, number> = {};
    for (const inv of openInvoices) {
      // outstanding = total − paid − credited (Phase 2b).
      m[inv.id] =
        toNumber(inv.totalAmount) - toNumber(inv.paidAmount) - toNumber(inv.creditedAmount);
    }
    return m;
  }, [openInvoices]);

  function toggle(invId: string) {
    setTicked((prev) => ({ ...prev, [invId]: !prev[invId] }));
    setAmounts((prev) => {
      if (prev[invId] !== undefined) return prev;
      // First tick defaults to the full outstanding.
      return { ...prev, [invId]: outstandingById[invId] ?? 0 };
    });
  }

  function setAllocAmount(invId: string, v: number) {
    setAmounts((prev) => ({ ...prev, [invId]: v }));
  }

  // Ticked rows carrying a positive amount → the submitted allocations.
  const allocations = useMemo(
    () =>
      openInvoices
        .filter((inv) => ticked[inv.id] && (amounts[inv.id] ?? 0) > 0)
        .map((inv) => ({ invoiceId: inv.id, amount: amounts[inv.id] ?? 0 })),
    [openInvoices, ticked, amounts],
  );

  const allocated = useMemo(() => allocations.reduce((sum, a) => sum + a.amount, 0), [allocations]);

  // Σ allocations must not exceed the cheque amount (the schema re-checks this).
  const overAllocated = amount > 0 && allocated > amount;

  // Per-invoice cap: a row may not allocate MORE than its own outstanding (the
  // backend rejects this at create). Build the ticked-row alloc map + matching
  // outstanding and reuse RecordReceiptModal's allocationError helper to flag the
  // first offending row + gate submit.
  const allocMap = useMemo(() => {
    const m: Record<string, number> = {};
    for (const inv of openInvoices) {
      if (ticked[inv.id]) m[inv.id] = amounts[inv.id] ?? 0;
    }
    return m;
  }, [openInvoices, ticked, amounts]);
  const allocatedOutstanding = useMemo(() => {
    const m: Record<string, number> = {};
    for (const id of Object.keys(allocMap)) m[id] = outstandingById[id] ?? 0;
    return m;
  }, [allocMap, outstandingById]);
  const rowError = allocationError(allocMap, allocatedOutstanding);
  // Ids whose allocation exceeds their outstanding — highlighted in the grid.
  const overRows = useMemo(() => {
    const s = new Set<string>();
    for (const [id, amt] of Object.entries(allocMap)) {
      if (amt > 0 && amt > (outstandingById[id] ?? 0)) s.add(id);
    }
    return s;
  }, [allocMap, outstandingById]);

  const disabled =
    !chequeNo ||
    !bankName ||
    !chequeDate ||
    !receivedDate ||
    amount <= 0 ||
    overAllocated ||
    rowError !== null ||
    submitting;

  function handleSubmit() {
    const payload: CreateArPdcInput = {
      customerId,
      chequeNo,
      bankName,
      ...(bankBranch ? { bankBranch } : {}),
      chequeDate,
      amount,
      receivedDate,
      ...(note ? { note } : {}),
      allocations,
    };
    onSubmit(payload);
  }

  return (
    <div style={modalBody}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <label style={modalLabel}>เลขที่เช็ค</label>
          <input
            className="input"
            aria-label="cheque-no"
            value={chequeNo}
            onChange={(e) => setChequeNo(e.target.value)}
            placeholder="เลขที่เช็ค"
            style={{ width: '100%', height: 38, fontSize: 14 }}
          />
        </div>
        <div>
          <label style={modalLabel}>จำนวนเงิน</label>
          <input
            type="number"
            className="input tnum"
            aria-label="amount"
            value={amount || ''}
            min={0}
            step={0.01}
            onChange={(e) => setAmount(Number(e.target.value))}
            style={{ width: '100%', height: 38, fontSize: 14, textAlign: 'right' }}
          />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <label style={modalLabel}>ธนาคาร</label>
          <input
            className="input"
            aria-label="bank-name"
            value={bankName}
            onChange={(e) => setBankName(e.target.value)}
            placeholder="ชื่อธนาคาร"
            style={{ width: '100%', height: 38, fontSize: 14 }}
          />
        </div>
        <div>
          <label style={modalLabel}>สาขาธนาคาร (optional)</label>
          <input
            className="input"
            aria-label="bank-branch"
            value={bankBranch}
            onChange={(e) => setBankBranch(e.target.value)}
            placeholder="สาขาธนาคาร"
            style={{ width: '100%', height: 38, fontSize: 14 }}
          />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <label style={modalLabel}>วันที่บนเช็ค (ลงวันที่ล่วงหน้า)</label>
          <input
            type="date"
            className="input"
            aria-label="cheque-date"
            value={chequeDate}
            onChange={(e) => setChequeDate(e.target.value)}
            style={{ width: '100%', height: 38, fontSize: 14 }}
          />
        </div>
        <div>
          <label style={modalLabel}>วันที่รับเช็ค</label>
          <input
            type="date"
            className="input"
            aria-label="received-date"
            value={receivedDate}
            onChange={(e) => setReceivedDate(e.target.value)}
            style={{ width: '100%', height: 38, fontSize: 14 }}
          />
        </div>
      </div>

      <div>
        <label style={modalLabel}>ใบแจ้งหนี้ที่ตั้งใจชำระด้วยเช็คนี้ (optional)</label>
        <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
          <table className="tbl">
            <thead>
              <tr>
                <th style={{ width: 36 }} />
                <th>เลขที่ใบแจ้งหนี้</th>
                <th>ครบกำหนด</th>
                <th className="num">ยอดค้าง</th>
                <th className="num" style={{ width: 140 }}>
                  จำนวนที่จัดสรร
                </th>
              </tr>
            </thead>
            <tbody>
              {openInvoices.length === 0 && (
                <tr>
                  <td colSpan={5} className="muted" style={{ textAlign: 'center', padding: 16 }}>
                    ไม่พบใบแจ้งหนี้คงค้าง
                  </td>
                </tr>
              )}
              {openInvoices.map((inv) => {
                const out = outstandingById[inv.id] ?? 0;
                const on = !!ticked[inv.id];
                const over = overRows.has(inv.id);
                return (
                  <tr key={inv.id}>
                    <td>
                      <input
                        type="checkbox"
                        aria-label={`select-${inv.voucherNo}`}
                        checked={on}
                        onChange={() => toggle(inv.id)}
                      />
                    </td>
                    <td className="code">{inv.voucherNo}</td>
                    <td>{String(inv.dueDate).slice(0, 10)}</td>
                    <td className="num tnum">{fmt(out, 2)}</td>
                    <td className="num">
                      <input
                        type="number"
                        className="input tnum"
                        aria-label={`amount-${inv.voucherNo}`}
                        value={on ? (amounts[inv.id] ?? 0) : ''}
                        disabled={!on}
                        min={0}
                        max={out}
                        step={0.01}
                        onChange={(e) => setAllocAmount(inv.id, Number(e.target.value))}
                        style={{
                          width: '100%',
                          height: 32,
                          fontSize: 13,
                          textAlign: 'right',
                          ...(over
                            ? { borderColor: 'var(--negative)', color: 'var(--negative)' }
                            : {}),
                        }}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div>
        <label style={modalLabel}>หมายเหตุ (optional)</label>
        <input
          className="input"
          aria-label="note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="หมายเหตุ"
          style={{ width: '100%', height: 38, fontSize: 14 }}
        />
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '10px 12px',
          borderRadius: 8,
          background: 'var(--brand-tint)',
          fontSize: 14,
          gap: 16,
        }}
      >
        <span className="muted">จัดสรรแล้ว {allocations.length} ใบ</span>
        <span>
          ยอดจัดสรรรวม{' '}
          <b
            className="tnum"
            style={{ fontSize: 16, color: overAllocated ? 'var(--negative)' : undefined }}
          >
            {fmt(allocated, 2)}
          </b>{' '}
          / {fmt(amount, 2)} บาท
        </span>
      </div>

      {overAllocated && (
        <div style={modalError}>ยอดจัดสรรรวมเกินจำนวนเงินบนเช็ค — กรุณาปรับยอดจัดสรร</div>
      )}

      {rowError && (
        <div style={modalError}>จำนวนที่จัดสรรเกินยอดคงค้างของใบแจ้งหนี้ — กรุณาปรับยอดจัดสรร</div>
      )}

      <div style={modalFooter}>
        <button type="button" className="btn primary" disabled={disabled} onClick={handleSubmit}>
          <Icon name="check" size={14} /> {submitting ? 'กำลังบันทึก…' : 'ลงทะเบียนเช็ครับ'}
        </button>
      </div>
    </div>
  );
}

export interface CreatePdcModalProps {
  companyId: string;
  // `| undefined` (not just optional) so callers may pass an explicit undefined
  // under exactOptionalPropertyTypes.
  presetCustomerId?: string | undefined;
  onClose: () => void;
}

export function CreatePdcModal({ companyId, presetCustomerId, onClose }: CreatePdcModalProps) {
  const [customerId, setCustomerId] = useState<string>(presetCustomerId ?? '');

  const { data: customers = [] } = useCustomers(companyId, true);
  const create = useCreateArPdc(companyId);

  // Open invoices for the chosen customer — POSTED only, then trimmed to rows
  // that still carry outstanding (a POSTED row can be fully paid pre-PAID flip).
  const { data: invoices = [] } = useArInvoices(
    companyId,
    customerId ? { customerId, status: 'POSTED' } : {},
  );
  const openInvoices = useMemo(
    () =>
      customerId
        ? invoices.filter(
            (r) =>
              toNumber(r.totalAmount) - toNumber(r.paidAmount) - toNumber(r.creditedAmount) > 0,
          )
        : [],
    [invoices, customerId],
  );

  const customerLabel = useMemo(() => {
    const m = new Map(customers.map((c) => [c.id, `${c.code} · ${c.nameTh}`]));
    return (id: string) => m.get(id) ?? id;
  }, [customers]);

  const customerOptions = useMemo(
    () => customers.map((c) => ({ value: c.id, label: `${c.code} · ${c.nameTh}` })),
    [customers],
  );

  async function handleSubmit(payload: CreateArPdcInput) {
    // Validate the wire shape (chequeNo/amount/date, Σ allocations ≤ amount)
    // before the POST.
    const data = CreateArPdcSchema.parse({ ...payload, customerId });
    await create.mutateAsync(data);
    onClose();
  }

  // WS8 R2 — standard AppModal (1200px) replaces the bespoke 760px chrome.
  // Container-only: CreatePdcForm keeps its body + in-body submit button.
  return (
    <AppModal title="ลงทะเบียนเช็ครับ" onClose={onClose}>
      <div style={{ padding: '0 22px 0' }}>
        <label style={modalLabel}>ลูกค้า</label>
        <SearchSelect
          value={customerId}
          onChange={setCustomerId}
          options={customerOptions}
          getLabel={customerLabel}
          placeholder="— เลือกลูกค้า —"
          disabled={!!presetCustomerId}
        />
      </div>

      {customerId ? (
        <CreatePdcForm
          key={customerId}
          openInvoices={openInvoices}
          onSubmit={(payload) => void handleSubmit(payload)}
          submitting={create.isPending}
          customerId={customerId}
        />
      ) : (
        <div style={{ ...modalBody, color: 'var(--text-muted)' }}>
          เลือกลูกค้าเพื่อแสดงใบแจ้งหนี้คงค้าง
        </div>
      )}

      {create.error && (
        <div style={{ ...modalError, padding: '0 22px 16px' }}>
          บันทึกไม่สำเร็จ: {create.error.message}
        </div>
      )}
    </AppModal>
  );
}
